import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";
import { supabase } from "@/lib/supabase";

export default function ContractPage() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signedAt, setSignedAt] = useState("");
  const [installerName, setInstallerName] = useState("Installer");
  const [companyName, setCompanyName] = useState("Your Company");

  useEffect(() => {
    const data = sessionStorage.getItem("signup_data");
    if (data) {
      const parsed = JSON.parse(data);
      setInstallerName(parsed.contact || "Installer");
      setCompanyName(parsed.company || "Your Company");
    }
  }, []);

  const handleSign = async () => {
    if (!agreed) return;
    setLoading(true);

    const now = new Date();
    const nowFormatted = now.toLocaleString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short"
    });

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      await supabase.from("installers").update({
        contract_signed: true,
        signed_at: now.toISOString(),
      }).eq("id", user.id);
    }

    setSignedAt(nowFormatted);
    setSigned(true);
    setLoading(false);

    sessionStorage.setItem("contract_signed", "true");
    sessionStorage.setItem("contract_signed_at", nowFormatted);
  };

  const handleContinue = () => {
    navigate("/payment-setup");
  };

  return (
    <AuthLayout
      title="Installer Partner Agreement"
      subtitle="Please read and sign the agreement below to continue."
      step={2}
      totalSteps={4}
    >
      {!signed ? (
        <div className="space-y-6">
          {/* Contract document */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 max-h-96 overflow-y-auto text-sm text-gray-700 leading-relaxed space-y-4">
            <div className="text-center mb-4">
              <h2 className="font-bold text-gray-900 text-base">SOLAR LEAD SOURCE — INSTALLER PARTNER AGREEMENT</h2>
              <p className="text-gray-500 text-xs mt-1">Effective upon digital signature</p>
            </div>

            <p><strong>1. PARTIES.</strong> This Installer Partner Agreement (&quot;Agreement&quot;) is entered into between Solar Lead Source (&quot;Company&quot;) and the installer identified during registration (&quot;Installer&quot;).</p>

            <p><strong>2. LEAD EXCLUSIVITY.</strong> Each lead purchased through the Solar Lead Source platform is sold exclusively to one (1) installer. Once purchased, the lead will not be resold or shared with any other party. The Installer acknowledges that leads are non-transferable.</p>

            <p><strong>3. LEAD QUALITY &amp; RETURNS.</strong> Solar Lead Source makes reasonable efforts to verify lead quality. Leads are generated from homeowners who have actively engaged with our solar savings tool. Refunds may be requested within 48 hours of purchase if the lead contact information is demonstrably invalid (disconnected phone, invalid email). Refunds are not issued for leads that do not convert to sales.</p>

            <p><strong>4. PAYMENT TERMS.</strong> Installer agrees to pay the listed price per lead at the time of purchase. All payments are processed via Stripe. No contracts or minimum purchase commitments are required. Prices are subject to change with 7 days notice.</p>

            <p><strong>5. CONTACT INFORMATION POLICY.</strong> Full homeowner contact details (name, phone, address) are only revealed after successful payment. Installer agrees not to share, resell, or distribute lead contact information to third parties.</p>

            <p><strong>6. SERVICE AREA.</strong> Installer agrees to only purchase leads within their registered service area. Solar Lead Source reserves the right to restrict purchases outside the registered area.</p>

            <p><strong>7. COMPLIANCE.</strong> Installer agrees to comply with all applicable federal, state, and local laws regarding solar sales, telemarketing (TCPA), and consumer protection. Installer is solely responsible for all communications with homeowners.</p>

            <p><strong>8. TERMINATION.</strong> Either party may terminate this agreement with 30 days written notice. Solar Lead Source reserves the right to immediately suspend access for violations of this agreement.</p>

            <p><strong>9. LIMITATION OF LIABILITY.</strong> Solar Lead Source&apos;s liability is limited to the amount paid for the specific lead in question. Solar Lead Source is not liable for lost profits, indirect, or consequential damages.</p>

            <p><strong>10. GOVERNING LAW.</strong> This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.</p>

            <p><strong>11. ENTIRE AGREEMENT.</strong> This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements.</p>

            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-xs text-gray-500">By digitally signing below, you acknowledge that you have read, understood, and agree to be bound by the terms of this Agreement. Your electronic signature is legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).</p>
            </div>
          </div>

          {/* Signature block */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Installer Name</span>
                <p className="font-semibold text-gray-900 mt-0.5">{installerName}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Company</span>
                <p className="font-semibold text-gray-900 mt-0.5">{companyName}</p>
              </div>
            </div>
          </div>

          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${agreed ? "bg-[#16a34a] border-[#16a34a]" : "border-gray-300 group-hover:border-green-400"}`}>
                {agreed && <i className="ri-check-line text-white text-xs"></i>}
              </div>
            </div>
            <span className="text-sm text-gray-700 leading-relaxed">
              I, <strong>{installerName}</strong> on behalf of <strong>{companyName}</strong>, have read and agree to the Installer Partner Agreement above. I understand this is a legally binding electronic signature.
            </span>
          </label>

          <button
            type="button"
            onClick={handleSign}
            disabled={!agreed || loading}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i> Processing signature...
              </span>
            ) : (
              <><i className="ri-pen-nib-line mr-2"></i>Sign Agreement &amp; Continue</>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <i className="ri-shield-check-line text-[#16a34a] text-3xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Agreement Signed!</h3>
            <p className="text-gray-500 text-sm">Your digital signature has been recorded and is legally binding.</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-left space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Signed by</span>
              <span className="font-semibold text-gray-900">{installerName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Company</span>
              <span className="font-semibold text-gray-900">{companyName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Signed at</span>
              <span className="font-semibold text-gray-900">{signedAt}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-semibold">
                <i className="ri-check-line"></i> Verified
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleContinue}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            Continue to Payment Setup <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
